import { Suspense, useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Link } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { BodyCopy } from '@xing-com/typography';

import { MetaData } from '../../components/meta-data';
import { PageError } from '../../components/page-error';
import { SkeletonLoggedOutIndustriesPage } from '../../components/skeleton';
import { useCompaniesIndustries } from '../../hooks/use-companies-industries';
import {
  getIndustryRoute,
  getIndustrySearchPageRoute,
} from '../../routes/routes';
import { formatAmount } from '../../utils/format-amount';
import { getStaticIndustryKey } from '../../utils/static-industry-key';
import { useNwtTrackingCompanies } from '../../utils/tracking';

import * as Styled from './oldIndustries.styles';

export const INDUSTRIES_LIMIT = 100;

type IndustryContentProps = {
  localizationValue: string;
  total: string;
};
const IndustryContent: FC<IndustryContentProps> = ({
  localizationValue,
  total,
}) => {
  return (
    <>
      <BodyCopy fontWeight="bold" size="small" noMargin>
        {localizationValue}
      </BodyCopy>
      <BodyCopy size="small" noMargin>
        <FormattedMessage id={'CP_TOTAL_COMPANIES'} values={{ total }} />
      </BodyCopy>
    </>
  );
};

type IndustriesContent = {
  link: string;
  className: string;
  total: string;
  localizationValue: string;
  id: string;
};
const LoggedInIndustryContent: FC<IndustriesContent> = ({
  link,
  localizationValue,
  total,
  id,
  className,
}) => {
  return (
    <a
      key={id}
      className={className}
      href={link}
      data-testid={`loggedin-industry-${id}`}
    >
      <IndustryContent localizationValue={localizationValue} total={total} />
    </a>
  );
};

const LoggedOutIndustryContent: FC<IndustriesContent> = ({
  link,
  localizationValue,
  total,
  id,
  className,
}) => {
  return (
    <Link
      key={id}
      className={className}
      to={link}
      data-testid={`loggedout-industry-${id}`}
    >
      <IndustryContent localizationValue={localizationValue} total={total} />
    </Link>
  );
};

const OldIndustriesInner: React.FC = () => {
  const intl = useIntl();
  const { isLoggedIn } = useLoginState();
  const { data, error } = useCompaniesIndustries({
    limit: INDUSTRIES_LIMIT,
  });
  const trackNwtEvent = useNwtTrackingCompanies();

  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/industries',
      event: 'viewed_screen',
    });
  }, []);

  if (error) return <PageError />;

  return (
    <Styled.Container>
      {data?.companiesIndustries?.collection.map((item) => {
        const id = item?.industry?.id as string;
        const localizationValue = item?.industry?.localizationValue as string;
        const total = item?.total as number;

        const link = isLoggedIn
          ? getIndustrySearchPageRoute({ id })
          : getIndustryRoute({
              id,
              name: intl.formatMessage({ id: getStaticIndustryKey(id) }),
            });

        const IndustryComponent = isLoggedIn
          ? LoggedInIndustryContent
          : LoggedOutIndustryContent;

        return (
          <IndustryComponent
            key={id}
            id={id}
            className="industry"
            link={link}
            total={formatAmount({ value: total, intl })}
            localizationValue={localizationValue}
          />
        );
      })}
    </Styled.Container>
  );
};

export const OldIndustries: FC = () => {
  const intl = useIntl();
  return (
    <>
      <MetaData
        index
        title={intl.formatMessage({ id: 'CP_100_META_TITLE_INDUSTRIES' })}
        description={intl.formatMessage({
          id: 'CP_INDUSTRIES_DESCRIPTION',
        })}
      />
      <Styled.IndustriTitle size="xlarge" as="h1">
        <FormattedMessage id="CP_100_META_TITLE_INDUSTRIES" />
      </Styled.IndustriTitle>
      <BodyCopy size="medium">
        <FormattedMessage id="CP_100_DIRECTORY_INTRO" />
      </BodyCopy>
      <BodyCopy fontWeight="bold" size="medium">
        <FormattedMessage id="CP_INDUSTRIES_DESCRIPTION" />
      </BodyCopy>
      <Suspense fallback={<SkeletonLoggedOutIndustriesPage />}>
        <OldIndustriesInner />
      </Suspense>
    </>
  );
};
